// Public forms, no session (SPEC-v2 V4, V7, V9; API.md 7.4, endpoints 62-63;
// ARCHITECTURE 20). One link = one form of one event.
import { z } from "zod";
import { countryCode } from "../countries";
import { GENDERS, STAKEHOLDER_TYPES, type PublicFormKind } from "../enums";
import { LIMITS } from "../limits";
import { email, optionalText, requiredText } from "./common";
import { answersSchema, type Question, type RegistrationFieldConfig } from "./events";

// ---------- Requests (63. POST /api/public/forms/:token) ----------

/** The bot trap: a hidden field people leave empty. When filled, the API answers success and stores nothing. */
const honeypot = z.string().max(1_000).default("");

const consentProcessing = z.literal(true, {
  errorMap: () => ({ message: "Tick the box to agree, or we cannot keep your details." }),
});

export const publicRegistrationBody = z
  .object({
    kind: z.literal("registration"),
    attending: z.boolean({ required_error: "Tell us whether you will attend." }),
    name: requiredText(LIMITS.expertName, "Name"),
    email,
    organisationName: optionalText(LIMITS.organisationName, "Organisation").default(""),
    position: optionalText(LIMITS.positionTitle, "Position").default(""),
    country: countryCode.nullable().default(null),
    gender: z.enum(GENDERS).nullable().default(null),
    stakeholderType: z.enum(STAKEHOLDER_TYPES).nullable().default(null),
    accessNeeds: optionalText(LIMITS.accessNeeds, "Access or dietary needs").default(""),
    answers: answersSchema.default({}),
    consentProcessing,
    consentContact: z.boolean().default(false),
    website: honeypot,
  })
  .strict();
export type PublicRegistrationBody = z.input<typeof publicRegistrationBody>;
export type PublicRegistrationInput = z.output<typeof publicRegistrationBody>;

export const publicCheckInBody = z
  .object({
    kind: z.literal("check_in"),
    name: requiredText(LIMITS.expertName, "Name"),
    email,
    organisationName: optionalText(LIMITS.organisationName, "Organisation").default(""),
    consentProcessing,
    consentContact: z.boolean().default(false),
    website: honeypot,
  })
  .strict();
export type PublicCheckInBody = z.input<typeof publicCheckInBody>;
export type PublicCheckInInput = z.output<typeof publicCheckInBody>;

/** Anonymous: no name, no email. */
export const publicFeedbackBody = z
  .object({
    kind: z.literal("feedback"),
    overall: z
      .number({ required_error: "Give a score from 1 to 5.", invalid_type_error: "Give a score from 1 to 5." })
      .int("Give a score from 1 to 5.")
      .min(1, "Give a score from 1 to 5.")
      .max(5, "Give a score from 1 to 5."),
    comment: optionalText(LIMITS.feedbackComment, "Comment").default(""),
    answers: answersSchema.default({}),
    website: honeypot,
  })
  .strict();
export type PublicFeedbackBody = z.input<typeof publicFeedbackBody>;
export type PublicFeedbackInput = z.output<typeof publicFeedbackBody>;

/** The body's `kind` must match the token's form, otherwise the API answers 404. */
export const publicFormBody = z.discriminatedUnion("kind", [publicRegistrationBody, publicCheckInBody, publicFeedbackBody]);
export type PublicFormBody = z.input<typeof publicFormBody>;
export type PublicFormInput = z.output<typeof publicFormBody>;

/** True when the raw body filled the bot trap. Check it BEFORE validation, so a bot never learns which field failed. */
export function isHoneypotFilled(raw: unknown): boolean {
  if (!raw || typeof raw !== "object") return false;
  const w = (raw as { website?: unknown }).website;
  return typeof w === "string" && w.trim() !== "";
}

/**
 * The event's "required" standard fields (V2) for a registration body. Fields
 * set to "off" are ignored by the API; gender is always optional. Returns an
 * error message per field name.
 */
export function checkRequiredFields(
  fields: RegistrationFieldConfig,
  body: Pick<PublicRegistrationInput, "organisationName" | "position" | "country" | "stakeholderType" | "accessNeeds">,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (fields.organisation === "required" && body.organisationName === "") errors.organisationName = "Enter your organisation.";
  if (fields.position === "required" && body.position === "") errors.position = "Enter your position.";
  if (fields.country === "required" && body.country === null) errors.country = "Choose your country.";
  if (fields.stakeholderType === "required" && body.stakeholderType === null)
    errors.stakeholderType = "Choose the type of your organisation.";
  if (fields.accessNeeds === "required" && body.accessNeeds === "") errors.accessNeeds = "Enter your access or dietary needs.";
  return errors;
}

// ---------- Response (62. GET /api/public/forms/:token) ----------

/** Exactly these keys, never more: no registration, contact, event id, org id or other token. */
export type PublicForm = {
  kind: PublicFormKind;
  open: boolean;
  /** e.g. "Registration is closed. Please contact events@example.org." */
  closedMessage: string | null;
  orgName: string;
  /** description only for "registration", else "" */
  event: { title: string; startDate: string; endDate: string; location: string; description: string };
  /** registration and check_in; null for feedback */
  fields: RegistrationFieldConfig | null;
  /** registration: questions; feedback: feedbackQuestions; check_in: [] */
  questions: Question[];
  /** registration: capacity reached; else false */
  full: boolean;
  /** optional is null for feedback */
  consent: { required: string; optional: string | null };
};

export const PUBLIC_FORM_KEYS = [
  "kind",
  "open",
  "closedMessage",
  "orgName",
  "event",
  "fields",
  "questions",
  "full",
  "consent",
] as const satisfies readonly (keyof PublicForm)[];

/** 63. POST answer. */
export type PublicFormAnswer = { ok: true; message: string };

export const PUBLIC_FORM_MESSAGES = {
  registered: "Thank you. You are registered.",
  declined: "Thank you for letting us know.",
  checkedIn: "You are checked in. Welcome.",
  feedback: "Thank you for your feedback.",
  full: "This event is full.",
} as const;

/** The consent statements shown on every public form (SPEC-v2 V4 AC2). */
export function consentStatements(orgName: string, kind: PublicFormKind): PublicForm["consent"] {
  return {
    required: `I agree that ${orgName} keeps my details to organise this event and report on it.`,
    optional: kind === "feedback" ? null : `${orgName} may contact me about future activities.`,
  };
}

/** The text of a closed form. */
export function closedFormMessage(kind: PublicFormKind, contactEmail: string): string {
  const what = kind === "registration" ? "Registration" : kind === "check_in" ? "Check-in" : "The feedback form";
  return contactEmail ? `${what} is closed. Please contact ${contactEmail}.` : `${what} is closed.`;
}
